import { Parameter } from '@shared/types';

// ============================================================================
// HSL/RGB Conversion Utilities
// ============================================================================

export interface HSL {
  h: number; // Hue: 0-360
  s: number; // Saturation: 0-1
  l: number; // Lightness: 0-1
}

export interface RGB {
  r: number; // Red: 0-1
  g: number; // Green: 0-1
  b: number; // Blue: 0-1
}

/**
 * Converts RGB (0-1 range) to HSL
 */
export function rgbToHsl(r: number, g: number, b: number): HSL {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) {
    return { h: 0, s: 0, l };
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h: number;
  switch (max) {
    case r:
      h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      break;
    case g:
      h = ((b - r) / d + 2) / 6;
      break;
    case b:
    default:
      h = ((r - g) / d + 4) / 6;
      break;
  }

  return { h: h * 360, s, l };
}

/**
 * Converts HSL to RGB (0-1 range)
 */
export function hslToRgb(h: number, s: number, l: number): RGB {
  // Normalize h to 0-360 range
  h = ((h % 360) + 360) % 360;

  if (s === 0) {
    return { r: l, g: l, b: l };
  }

  const hueToRgb = (p: number, q: number, t: number): number => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const hNorm = h / 360;

  return {
    r: hueToRgb(p, q, hNorm + 1 / 3),
    g: hueToRgb(p, q, hNorm),
    b: hueToRgb(p, q, hNorm - 1 / 3),
  };
}

/**
 * Gets the perceived brightness/luminance of a color (0-1)
 * Uses the relative luminance formula for human perception
 */
export function getColorBrightness(r: number, g: number, b: number): number {
  // Relative luminance formula (ITU-R BT.709)
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// ============================================================================
// Color Harmony Types and Algorithms
// ============================================================================

export type ColorHarmonyType =
  | 'complementary'
  | 'analogous'
  | 'triadic'
  | 'split-complementary'
  | 'tetradic'
  | 'monochromatic';

/**
 * Generates harmonious hue offsets based on the harmony type
 * Returns an array of hue offsets in degrees
 */
export function getHarmonyHueOffsets(
  harmonyType: ColorHarmonyType,
  count: number,
): number[] {
  switch (harmonyType) {
    case 'complementary':
      // Two colors opposite on the wheel
      return distributeOffsets([0, 180], count);

    case 'analogous':
      // Colors next to each other (30 degrees apart)
      return distributeOffsets(
        Array.from({ length: Math.min(count, 5) }, (_, i) => i * 30 - 60),
        count,
      );

    case 'triadic':
      // Three colors evenly spaced (120 degrees apart)
      return distributeOffsets([0, 120, 240], count);

    case 'split-complementary':
      // Base color plus two colors adjacent to its complement
      return distributeOffsets([0, 150, 210], count);

    case 'tetradic':
      // Four colors in rectangle pattern
      return distributeOffsets([0, 60, 180, 240], count);

    case 'monochromatic':
      // All same hue, just different saturation/lightness
      return Array(count).fill(0);

    default:
      return distributeOffsets([0, 120, 240], count);
  }
}

/**
 * Distributes base offsets to fill the required count
 * If count > base offsets, it interpolates between them
 */
function distributeOffsets(baseOffsets: number[], count: number): number[] {
  if (count <= baseOffsets.length) {
    return baseOffsets.slice(0, count);
  }

  const result: number[] = [...baseOffsets];
  let index = 0;

  while (result.length < count) {
    // Add small variations to existing offsets
    const variation = ((result.length - baseOffsets.length + 1) * 15) % 30;
    result.push(baseOffsets[index % baseOffsets.length] + variation);
    index++;
  }

  return result;
}

/**
 * Randomly selects a color harmony type
 */
export function getRandomHarmonyType(): ColorHarmonyType {
  const types: ColorHarmonyType[] = [
    'complementary',
    'analogous',
    'triadic',
    'split-complementary',
    'tetradic',
  ];
  return types[Math.floor(Math.random() * types.length)];
}

/**
 * Generates a set of harmonious colors
 * @param count Number of colors to generate
 * @param originalBrightnesses Array of original brightness values to preserve (0-1)
 * @param harmonyType Optional harmony type (random if not specified)
 * @returns Array of RGB colors (0-1 range)
 */
export function generateHarmoniousColors(
  count: number,
  originalBrightnesses: number[],
  harmonyType?: ColorHarmonyType,
): RGB[] {
  if (count === 0) return [];

  // Choose a random harmony type if not specified
  const harmony = harmonyType || getRandomHarmonyType();

  // Generate a random base hue
  const baseHue = Math.random() * 360;

  // Get hue offsets for this harmony type
  const hueOffsets = getHarmonyHueOffsets(harmony, count);

  // Generate a base saturation (vibrant but not too intense)
  // Range: 0.5 - 0.85 for nice, vivid colors
  const baseSaturation = 0.5 + Math.random() * 0.35;

  // Generate colors with the harmony scheme
  const colors: RGB[] = [];

  for (let i = 0; i < count; i++) {
    const hue = (baseHue + hueOffsets[i]) % 360;

    // Get the original brightness for this color position
    const targetBrightness = originalBrightnesses[i] ?? 0.5;

    // Convert target brightness to lightness
    // Brightness and lightness are related but not identical
    // We'll use brightness as a guide for lightness
    let lightness = targetBrightness;

    // Clamp lightness to avoid pure white or pure black
    lightness = Math.max(0.15, Math.min(0.85, lightness));

    // Adjust saturation based on lightness
    // Very light or very dark colors need less saturation to look good
    let saturation = baseSaturation;
    if (lightness < 0.3 || lightness > 0.7) {
      saturation = baseSaturation * 0.8;
    }

    // For monochromatic, vary saturation and lightness more
    if (harmony === 'monochromatic') {
      saturation = 0.3 + Math.random() * 0.5;
    }

    const rgb = hslToRgb(hue, saturation, lightness);
    colors.push(rgb);
  }

  return colors;
}

/**
 * Generates a single random but aesthetically pleasing color
 * Avoids muddy colors by ensuring decent saturation
 */
export function generateRandomPrettyColor(targetBrightness?: number): RGB {
  const hue = Math.random() * 360;
  const saturation = 0.5 + Math.random() * 0.35; // 0.5 - 0.85

  let lightness: number;
  if (targetBrightness !== undefined) {
    lightness = Math.max(0.15, Math.min(0.85, targetBrightness));
  } else {
    lightness = 0.35 + Math.random() * 0.3; // 0.35 - 0.65
  }

  return hslToRgb(hue, saturation, lightness);
}

// ============================================================================
// OpenSCAD Color Format Conversion
// ============================================================================

/**
 * All named colors supported by OpenSCAD (SVG/CSS color list, case-insensitive).
 * Values are lowercase 6-digit hex strings without '#'.
 * OpenSCAD also supports "transparent" as a special case.
 */
export const OPENSCAD_NAMED_COLORS: Record<string, string> = {
  // Purples
  lavender: 'e6e6fa',
  thistle: 'd8bfd8',
  plum: 'dda0dd',
  violet: 'ee82ee',
  orchid: 'da70d6',
  fuchsia: 'ff00ff',
  magenta: 'ff00ff',
  mediumorchid: 'ba55d3',
  mediumpurple: '9370db',
  blueviolet: '8a2be2',
  darkviolet: '9400d3',
  darkorchid: '9932cc',
  darkmagenta: '8b008b',
  purple: '800080',
  indigo: '4b0082',
  darkslateblue: '483d8b',
  slateblue: '6a5acd',
  mediumslateblue: '7b68ee',
  // Reds
  indianred: 'cd5c5c',
  lightcoral: 'f08080',
  salmon: 'fa8072',
  darksalmon: 'e9967a',
  lightsalmon: 'ffa07a',
  red: 'ff0000',
  crimson: 'dc143c',
  firebrick: 'b22222',
  darkred: '8b0000',
  // Blues
  aqua: '00ffff',
  cyan: '00ffff',
  lightcyan: 'e0ffff',
  paleturquoise: 'afeeee',
  aquamarine: '7fffd4',
  turquoise: '40e0d0',
  mediumturquoise: '48d1cc',
  darkturquoise: '00ced1',
  cadetblue: '5f9ea0',
  steelblue: '4682b4',
  lightsteelblue: 'b0c4de',
  powderblue: 'b0e0e6',
  lightblue: 'add8e6',
  skyblue: '87ceeb',
  lightskyblue: '87cefa',
  deepskyblue: '00bfff',
  dodgerblue: '1e90ff',
  cornflowerblue: '6495ed',
  royalblue: '4169e1',
  blue: '0000ff',
  mediumblue: '0000cd',
  darkblue: '00008b',
  navy: '000080',
  midnightblue: '191970',
  // Pinks
  pink: 'ffc0cb',
  lightpink: 'ffb6c1',
  hotpink: 'ff69b4',
  deeppink: 'ff1493',
  mediumvioletred: 'c71585',
  palevioletred: 'db7093',
  // Greens
  greenyellow: 'adff2f',
  chartreuse: '7fff00',
  lawngreen: '7cfc00',
  lime: '00ff00',
  limegreen: '32cd32',
  palegreen: '98fb98',
  lightgreen: '90ee90',
  mediumspringgreen: '00fa9a',
  springgreen: '00ff7f',
  mediumseagreen: '3cb371',
  seagreen: '2e8b57',
  forestgreen: '228b22',
  green: '008000',
  darkgreen: '006400',
  yellowgreen: '9acd32',
  olivedrab: '6b8e23',
  olive: '808000',
  darkolivegreen: '556b2f',
  mediumaquamarine: '66cdaa',
  darkseagreen: '8fbc8f',
  lightseagreen: '20b2aa',
  darkcyan: '008b8b',
  teal: '008080',
  // Oranges
  coral: 'ff7f50',
  tomato: 'ff6347',
  orangered: 'ff4500',
  darkorange: 'ff8c00',
  orange: 'ffa500',
  // Yellows
  gold: 'ffd700',
  yellow: 'ffff00',
  lightyellow: 'ffffe0',
  lemonchiffon: 'fffacd',
  lightgoldenrodyellow: 'fafad2',
  papayawhip: 'ffefd5',
  moccasin: 'ffe4b5',
  peachpuff: 'ffdab9',
  palegoldenrod: 'eee8aa',
  khaki: 'f0e68c',
  darkkhaki: 'bdb76b',
  // Browns
  cornsilk: 'fff8dc',
  blanchedalmond: 'ffebcd',
  bisque: 'ffe4c4',
  navajowhite: 'ffdead',
  wheat: 'f5deb3',
  burlywood: 'deb887',
  tan: 'd2b48c',
  rosybrown: 'bc8f8f',
  sandybrown: 'f4a460',
  goldenrod: 'daa520',
  darkgoldenrod: 'b8860b',
  peru: 'cd853f',
  chocolate: 'd2691e',
  saddlebrown: '8b4513',
  sienna: 'a0522d',
  brown: 'a52a2a',
  maroon: '800000',
  // Whites
  white: 'ffffff',
  snow: 'fffafa',
  honeydew: 'f0fff0',
  mintcream: 'f5fffa',
  azure: 'f0ffff',
  aliceblue: 'f0f8ff',
  ghostwhite: 'f8f8ff',
  whitesmoke: 'f5f5f5',
  seashell: 'fff5ee',
  beige: 'f5f5dc',
  oldlace: 'fdf5e6',
  floralwhite: 'fffaf0',
  ivory: 'fffff0',
  antiquewhite: 'faebd7',
  linen: 'faf0e6',
  lavenderblush: 'fff0f5',
  mistyrose: 'ffe4e1',
  // Grays (both spellings accepted)
  gainsboro: 'dcdcdc',
  lightgrey: 'd3d3d3',
  lightgray: 'd3d3d3',
  silver: 'c0c0c0',
  darkgray: 'a9a9a9',
  darkgrey: 'a9a9a9',
  gray: '808080',
  grey: '808080',
  dimgray: '696969',
  dimgrey: '696969',
  lightslategray: '778899',
  lightslategrey: '778899',
  slategray: '708090',
  slategrey: '708090',
  darkslategray: '2f4f4f',
  darkslategrey: '2f4f4f',
  black: '000000',
  // Special
  transparent: '000000', // alpha=0, handled separately in scadStringToAlpha
};

/**
 * Parses an OpenSCAD color string value (without outer quotes) to a #RRGGBB hex string.
 * Supports:
 * - Named colors: "red", "Blue", "DARKGREEN" (case-insensitive)
 * - 3-digit hex: "#F00" → #FF0000
 * - 4-digit hex with alpha: "#F00F" → #FF0000 (alpha extracted separately)
 * - 6-digit hex: "#FF0000"
 * - 8-digit hex with alpha: "#FF0000FF" → #FF0000 (alpha extracted separately)
 * Returns null if the value is not a recognized OpenSCAD color format.
 */
export function scadStringToHex(value: string): string | null {
  const trimmed = value.trim();

  if (trimmed.startsWith('#')) {
    const digits = trimmed.slice(1);
    if (/^[0-9a-fA-F]+$/.test(digits)) {
      if (digits.length === 3) {
        // #RGB → #RRGGBB
        const r = digits[0] + digits[0];
        const g = digits[1] + digits[1];
        const b = digits[2] + digits[2];
        return `#${r}${g}${b}`.toUpperCase();
      } else if (digits.length === 4) {
        // #RGBA → #RRGGBB (alpha ignored here, extracted by scadStringToAlpha)
        const r = digits[0] + digits[0];
        const g = digits[1] + digits[1];
        const b = digits[2] + digits[2];
        return `#${r}${g}${b}`.toUpperCase();
      } else if (digits.length === 6) {
        return `#${digits}`.toUpperCase();
      } else if (digits.length === 8) {
        // #RRGGBBAA → #RRGGBB
        return `#${digits.slice(0, 6)}`.toUpperCase();
      }
    }
    return null;
  }

  // Named color lookup (case-insensitive)
  const key = trimmed.toLowerCase();
  const hex = OPENSCAD_NAMED_COLORS[key];
  return hex ? `#${hex}`.toUpperCase() : null;
}

/**
 * Extracts the alpha value from an OpenSCAD color string (without outer quotes).
 * Returns 0 for "transparent", the alpha channel from 4/8-digit hex strings,
 * and 1.0 for all other cases (named colors, 3/6-digit hex).
 */
export function scadStringToAlpha(value: string): number {
  const trimmed = value.trim();

  if (trimmed.toLowerCase() === 'transparent') return 0.0;

  if (trimmed.startsWith('#')) {
    const digits = trimmed.slice(1);
    if (/^[0-9a-fA-F]+$/.test(digits)) {
      if (digits.length === 4) {
        // #RGBA — expand single hex digit to 0-1
        return parseInt(digits[3] + digits[3], 16) / 255;
      } else if (digits.length === 8) {
        // #RRGGBBAA
        return parseInt(digits.slice(6, 8), 16) / 255;
      }
    }
  }

  return 1.0;
}

/**
 * Returns true if the given string (without outer quotes) is a recognized
 * OpenSCAD color format (named color or hex string).
 */
export function isScadColorString(value: string): boolean {
  return scadStringToHex(value) !== null;
}

/**
 * Converts OpenSCAD color format (array of 3-4 numbers or a color string) to hex string.
 * Supports:
 * - [0-1] float range: [1, 0.5, 0]
 * - [0-255] integer range: [255, 128, 0] (detected if any value > 1)
 * - Named color string: "red", "Blue"
 * - Hex color string: "#FF0000", "#F00", "#FF0000FF", "#F00F"
 */
export function scadToHex(value: Parameter['value']): string {
  // Handle string color values (named colors, hex strings)
  if (typeof value === 'string') {
    return scadStringToHex(value) ?? '#000000';
  }

  if (!Array.isArray(value) || value.length < 3) return '#000000';

  const numbers = value.map(Number);
  if (numbers.some(isNaN)) return '#000000';

  // Detect if using 0-255 range (any value > 1 implies 255 scale)
  const is255 = numbers.some((n) => n > 1);

  const [r, g, b] = numbers.slice(0, 3).map((n) => {
    const val = is255 ? n : n * 255;
    return Math.round(Math.max(0, Math.min(255, val)));
  });

  const toHex = (n: number) => n.toString(16).padStart(2, '0');

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}

/**
 * Converts hex string to OpenSCAD vector format
 * Returns array of floats [0-1, 0-1, 0-1] by default
 */
export function hexToScad(hex: string): number[] {
  const cleanHex = hex.replace('#', '');

  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  // Format to reasonable precision to avoid massive float strings
  const format = (n: number) => parseFloat(n.toFixed(3));

  return [format(r), format(g), format(b)];
}

/**
 * Extracts the alpha value from an OpenSCAD color value.
 * Handles both array format [r, g, b, a] and string format ("transparent", "#FF000080").
 * Returns 1.0 if no alpha component is present.
 */
export function scadToAlpha(value: Parameter['value']): number {
  if (typeof value === 'string') {
    return scadStringToAlpha(value);
  }
  if (!Array.isArray(value) || value.length < 4) return 1.0;
  const a = Number(value[3]);
  return isNaN(a) ? 1.0 : Math.max(0, Math.min(1, a));
}

/**
 * Converts hex string + alpha to OpenSCAD vector format.
 * Returns [r, g, b] if alpha is 1.0, or [r, g, b, a] if alpha < 1.0.
 */
export function hexToScadWithAlpha(hex: string, alpha: number): number[] {
  const rgb = hexToScad(hex);
  const format = (n: number) => parseFloat(n.toFixed(3));
  if (alpha < 1.0) {
    return [...rgb, format(alpha)];
  }
  return rgb;
}
